import styled from '@emotion/styled';
import {
  formatOffsetInMeterBasedOnUnitType,
  formatSpeed,
  formatTimeDuration,
} from '@racemap/utilities/formatting';
import {
  type Geofence,
  type PreparedStarterInfo,
  getVirtualRaceDistance,
  getVirtualRaceDuration,
  sortTimes,
  testEventHasVirtDuration,
} from '@racemap/utilities/functions/timing';
import type { RacemapEvent } from '@racemap/utilities/types/events';
import type { Splits } from '@racemap/utilities/types/types';
import type { Immutable } from 'immer';
import { ICON_CDN_PATH } from '../../../BasicComponents/IconChooser/IconChooser';
import type { Column } from '../TableBody';
import { DoubleCell } from './BasicCell';
import { LARGE_TIME_COL_WIDTH, SMALL_TIME_COL_WIDTH, TimesColumHeaderTitle } from './TimesCol';

function TimesFinishColComponent({
  starterInfo,
  event,
  splits,
  timekeepings,
}: {
  starterInfo: Immutable<PreparedStarterInfo>;
  event: Immutable<RacemapEvent>;
  splits: Immutable<Splits>;
  timekeepings: Immutable<Array<Geofence>>;
}) {
  const eventVirtualRaceDistance = getVirtualRaceDistance(event, splits);
  const eventVirtualRaceDuration = getVirtualRaceDuration(event);
  const eventHasVirtDuration = testEventHasVirtDuration(event);
  const unit = event.playerOptions.unitType;
  const starterResult = starterInfo?.results.at(timekeepings.length - 1);

  return (
    <DoubleCell>
      <div>
        {eventHasVirtDuration
          ? formatOffsetInMeterBasedOnUnitType(starterResult?.relativeProgress, unit)
          : formatTimeDuration(starterResult?.relativeProgress)}
      </div>
      <Speed>
        {starterResult?.relativeProgress != null
          ? formatSpeed(
              eventHasVirtDuration
                ? starterResult?.relativeProgress / eventVirtualRaceDuration
                : eventVirtualRaceDistance / starterResult?.relativeProgress,
              unit,
            )
          : '--'}
      </Speed>
    </DoubleCell>
  );
}

export function getTimesFinishCol(
  event: Immutable<RacemapEvent>,
  splits: Immutable<Splits>,
  timekeepings: Immutable<Array<Geofence>>,
  windowWidth: number,
): Column {
  const eventHasVirtDuration = testEventHasVirtDuration(event);
  const unit = event.playerOptions.unitType;
  const visibleElements = event.modules.timing.visibleElements;
  const finishGeofence: Immutable<Geofence> | undefined = timekeepings.at(timekeepings.length - 1);
  const splitIconName = splits?.find((split) => split.id === finishGeofence?.id)?.icon;
  const name = splits.length === 0 || finishGeofence == null ? 'Finish' : finishGeofence.name;
  const icon = splitIconName;
  const distance = eventHasVirtDuration
    ? formatTimeDuration(finishGeofence?.timeOffset)
    : formatOffsetInMeterBasedOnUnitType(finishGeofence?.offset, unit);

  return {
    key: 'times_finish',
    title: (
      <TimesColumHeaderTitle
        headerContent={visibleElements.headerContent}
        name={name}
        icon={icon != null ? icon : `${ICON_CDN_PATH}/finish-01_tickless.svg`}
        distance={distance}
      />
    ),
    width:
      windowWidth < 990 || timekeepings.length > 6 ? SMALL_TIME_COL_WIDTH : LARGE_TIME_COL_WIDTH,
    render: function renderer(row: Immutable<PreparedStarterInfo>) {
      return (
        <TimesFinishColComponent
          event={event}
          timekeepings={timekeepings}
          starterInfo={row}
          splits={splits}
        />
      );
    },
    sorter: (a: Immutable<PreparedStarterInfo>, b: Immutable<PreparedStarterInfo>) =>
      eventHasVirtDuration
        ? b?.results[timekeepings?.length - 1]?.relativeProgress -
          a?.results[timekeepings?.length - 1]?.relativeProgress
        : sortTimes(
            b?.results[timekeepings?.length - 1]?.relativeProgress,
            a?.results[timekeepings?.length - 1]?.relativeProgress,
          ),
  };
}

const Speed = styled.div`
  opacity: 0.7;
  font-size: 10px;
`;
